import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import "../../assets/custom/css/cti.css";
import NavBar from "./NavBar";
import ConferenceScreen from "./screens/ConferenceScreen";
import IdleScreen from "./screens/IdleScreen";
import OnCallScreen from "./screens/OnCallScreen";
import RingingScreen from "./screens/RingingScreen";
import Header from "../../components/partials/dashboard/HeaderStyle/header";
import { Modal } from "react-bootstrap";
import { Form } from "react-bootstrap";
import { Button } from "primereact/button";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { API_AGENT_DASHBOARD_CAMPAIGNS } from "../../service/url";
import { USER_ROLES } from '../../utilities/constant';
import { postApiCall, getApiCall } from "../../service/httpCall";
import {
  API_BREAK_LIST,
  USER_BREAKS_EDIT,
  USER_BREAKS_CREATE,
  API_AGENT_DASHBOARD_CALLS,
  API_AGENT_IDLE_STATS,
} from "../../service/url";
import { toast } from "react-toastify";
import { BreakContext } from "../../router/context/BreakContext";


const durationToSeconds = (duration) => {
  const minutes = parseInt(duration, 10);
  return minutes * 60;
};

function CTI() {
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const handleShow = () => setShowBreakModal(true);
  const handleClose = () => setShowBreakModal(false);
  const [screen, setScreen] = useState("idle");
  const [theme, setTheme] = useState(
    () => localStorage.getItem("cti-theme") || "light",
  );
  const [activeTab, setActiveTab] = useState("log");
  const [dialNum, setDialNum] = useState("");
  const [holdOn, setHoldOn] = useState(false);
  const [muteOn, setMuteOn] = useState(false);
  const [idleStatus, setIdleStatus] = useState("READY");
  const [idleSeconds, setIdleSeconds] = useState(0);
  const [callSeconds, setCallSeconds] = useState(272);
  const [confSeconds, setConfSeconds] = useState(728);
  const [breaksData, setBreaksData] = useState([]);
  const [userData, setUserData] = useState(null);
  const { breakEndTime, userStatus, startBreak, stopBreak, setSelectedBreakId, selectedBreakId, countdown} = useContext(BreakContext);

  const [loggedUserRole, setLoggedUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentCallsData, setAgentCallsData] = useState([]);
  const [webrtcStatus, setWebrtcStatus] = useState(null);
  const [idleStartedAt, setIdleStartedAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    const interval = setInterval(() => {
      const status = sessionStorage.getItem("webrtcPhoneStatus");
      setWebrtcStatus(status);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  const parsedStatus = useMemo(() => {
    if (!webrtcStatus)
      return {
        success: false,
        code: "DISCONNECTED",
        message: "Dialer is Disconnected",
      };
  
    if (!webrtcStatus.includes("-")) {
      return {
        success: true,
        code: "READY",
        message: "Dialer connected",
      };
    }
  
    const [code, message] = webrtcStatus.split("-");
  
    return {
      success: false,
      code: code || "ERROR",
      message: message || "Connection error",
    };
  }, [webrtcStatus]);

  useEffect(() => {
    localStorage.setItem("cti-theme", theme);
  }, [theme]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (screen === "idle") setIdleSeconds((value) => value + 1);
      if (screen === "oncall") setCallSeconds((value) => value + 1);
      if (screen === "conference") setConfSeconds((value) => value + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [screen]);

  useEffect(() => {
    if (screen === "idle" && parsedStatus?.success) {
      setIdleStartedAt(new Date().toISOString());
      setIdleSeconds(0);
    }
  }, [screen, parsedStatus]);

  const statusMeta = useMemo(() => {
    if (screen === "ringing") return { text: "RINGING", dotClass: "ring" };
    if (screen === "oncall") return { text: "ON CALL", dotClass: "busy" };
    if (screen === "conference") return { text: "CONF", dotClass: "busy" };
    return { text: "READY", dotClass: "" };
  }, [screen]);

  const fetchUserData = async () => {
    try {
      const user = JSON.parse(await AsyncStorage.getItem("user"));
      if (user) {
        setUserData(user);
      }
    } catch (error) {
      console.log("Error getting logged in user data", error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    getApiCall(`${API_BREAK_LIST}?status=1`, {})
      .then(onListSuccess)
      .catch(onError);
  }, []);

  function onError(err) {
    console.log(err);
  }

  function onListSuccess(resp) {
    setBreaksData(resp);
  }

  useEffect(() => {
      const fetchUserDataAndCampaigns = async () => {
        const rawUser = await AsyncStorage.getItem("user");
    
        if (!rawUser) {
          console.error("User data not found in AsyncStorage");
          return;
        }
    
        let user;
        try {
          user = JSON.parse(rawUser);
        } catch (e) {
          console.error("Invalid JSON in AsyncStorage user:", e);
          return;
        }
    
        const role = user?.role;
        setLoggedUserRole(role);
    
        if (role === USER_ROLES.CALL_CENTRE_AGENT) {
          const id = user?.agentData?.id;
          setLoggedUserId(id);
          fetchCampaigns(role, id);
          fetchAgentCallsData(role, id);
        }
      };
    
      fetchUserDataAndCampaigns();
    }, []);

    const fetchCampaigns = async (role, agentId = null) => {
      setLoading(true);
      let url = API_AGENT_DASHBOARD_CAMPAIGNS;
    
      if (role === USER_ROLES.CALL_CENTRE_AGENT && agentId) {
        url += `?agent_id=${agentId}`;
      }
      try {
        const res = await getApiCall(url, {});
        onCampaignListSuccess(res);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    };
    
    function onCampaignListSuccess(resp) {
      setCampaigns(resp);
    }

    const fetchAgentCallsData = async (role, agentId = null) => {
      setLoading(true);
      let url = API_AGENT_DASHBOARD_CALLS;
    
      if (role === USER_ROLES.CALL_CENTRE_AGENT && agentId) {
        url = url.replace(":id", agentId);
      }
      try {
        const res = await getApiCall(url, {});
        onAgentCallsSuccess(res);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    };

    function onAgentCallsSuccess(resp) {
      setAgentCallsData(resp);
    }

    useEffect(() => {
      if (!loggedUserRole || !loggedUserId || (loggedUserRole !== USER_ROLES.CALL_CENTRE_AGENT)) return;
      let isMounted = true;
      let timeoutId;
      const poll = async () => {
        if (!isMounted) return;
        await fetchAgentCallsData(loggedUserRole, loggedUserId);
        await fetchCampaigns(loggedUserRole, loggedUserId);
        timeoutId = setTimeout(poll, 6000);
      };
      poll();
      return () => {
        isMounted = false;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [loggedUserRole, loggedUserId]);

    useEffect(() => {
      if (parsedStatus?.success) {
        const timeout = setTimeout(() => {
          try {
            require("../../components/partials/dashboard/CallIcon/demo-1");
          } catch (err) {
            console.error("Error loading demo file:", err);
          }
        }, 1000);
        return () => clearTimeout(timeout);
      }
    }, [parsedStatus]);

  const sendAgentIdleStats = useCallback(async () => {
    if (!parsedStatus?.success) return;
    if (!loggedUserId || screen !== "idle") {
      return;
    }

    const payload = {
      from: idleStartedAt || new Date().toISOString(),
      to: new Date().toISOString(),
      agent_id: loggedUserId,
    };

    try {
      await postApiCall(API_AGENT_IDLE_STATS, payload);
    } catch (error) {
      console.error("Failed to save agent idle stats", error);
    }
  }, [idleStartedAt, loggedUserId, screen]);

  const handlePlaceCall = useCallback(async () => {
    // await sendAgentIdleStats();
    setScreen("oncall");
  }, [sendAgentIdleStats]);

  const handleRinging = useCallback((value) => {
    if (value) {
      setDialNum(value);
    }
    setScreen("ringing");
  }, []);

  const handleAcceptCall = useCallback(async () => {
    // await sendAgentIdleStats();
    setScreen("oncall");
  }, [sendAgentIdleStats]);

  return (
    <>
      <div className="cti-app-shell" data-theme={theme}>
        <NavBar
          screen={screen}
          onScreenChange={setScreen}
          statusMeta={statusMeta}
          theme={theme}
          userData={userData}
          agentCallsData={agentCallsData}
          onToggleTheme={() =>
            setTheme((value) => (value === "light" ? "dark" : "light"))
          }
        />

        {screen === "idle" && (
          <IdleScreen
            idleSeconds={idleSeconds}
            idleStatus={idleStatus}
            activeTab={activeTab}
            dialNum={dialNum}
            breakEndTime={breakEndTime}
            countdown={countdown}
            breaksData={breaksData}
            selectedBreakId={selectedBreakId}
            onDial={(value) => setDialNum((current) => current + value)}
            onDialBack={() => setDialNum((current) => current.slice(0, -1))}
            onTabChange={setActiveTab}
            onStatusChange={setIdleStatus}
            onPlaceCall={handlePlaceCall}
            onRinging={handleRinging}
            onSetBreakClick={() => setShowBreakModal(true)}
            onRemoveBreakClick={() => setShowCancelModal(true)}
            userData={userData}
            campaigns={campaigns}
            theme={theme}
            userStatus={userStatus}
            agentCallsData={agentCallsData}
            loggedUserRole={loggedUserRole}
            loggedUserId={loggedUserId}
          />
        )}

        {screen === "ringing" && (
          <RingingScreen onAccept={handleAcceptCall} agentCallsData={agentCallsData}/>
        )}

        {screen === "oncall" && (
          <OnCallScreen
            callSeconds={callSeconds}
            holdOn={holdOn}
            muteOn={muteOn}
            onToggleHold={() => setHoldOn((value) => !value)}
            onToggleMute={() => setMuteOn((value) => !value)}
            onGoConference={() => setScreen("conference")}
            onEndCall={() => setScreen("idle")}
            campaigns={campaigns}
            agentCallsData={agentCallsData}
          />
        )}

        {screen === "conference" && (
          <ConferenceScreen
            confSeconds={confSeconds}
            onLeave={() => setScreen("oncall")}
            onEndAll={() => setScreen("idle")}
            agentCallsData={agentCallsData}
          />
        )}
      </div>
      {/* Break Selection Modal */}
      <Modal show={showBreakModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="breakSelect">
            <Form.Label>
              Select a break:
              <span style={{ color: "red", margin: "0 4px" }}>*</span>
            </Form.Label>
            <Form.Select
              value={selectedBreakId}
              onChange={(e) => setSelectedBreakId(e.target.value)}
            >
              <option value="">Select Break</option>
              {breaksData.map((brk) => (
                <option key={brk.id} value={brk.id}>
                  {brk.title} - ({brk.duration} Mins)
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{ marginRight: "20px", backgroundColor: "grey" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: "#1789e5ff" }}
            onClick={() => {
              startBreak(selectedBreakId);
              handleClose();
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Break Modal */}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Break</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to cancel the break?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelModal(false)}
            style={{ marginRight: "20px", backgroundColor: "grey" }}
          >
            No
          </Button>
          <Button
            variant="danger"
              onClick={() => {
                const user = JSON.parse(localStorage.getItem("user"));
                stopBreak(user?.id);
                setShowCancelModal(false)
              }}
              style={{ backgroundColor: "#1789e5ff" }}
          >
            Yes, Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CTI;
